import type { BakedBlockPlacement } from '@tsagko/label-contracts'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

type HAlign = BakedBlockPlacement['hAlign']
type VAlign = BakedBlockPlacement['vAlign']

interface AlignmentGridProps {
  hAlign: HAlign
  vAlign: VAlign
  onChange: (next: { hAlign: HAlign; vAlign: VAlign }) => void
  disabled?: boolean
}

const ROWS: VAlign[] = ['top', 'center', 'bottom']
const COLS: HAlign[] = ['left', 'center', 'right']

const GLYPHS: Record<`${HAlign}-${VAlign}`, string> = {
  'left-top': '↖',
  'center-top': '↑',
  'right-top': '↗',
  'left-center': '←',
  'center-center': '·',
  'right-center': '→',
  'left-bottom': '↙',
  'center-bottom': '↓',
  'right-bottom': '↘',
}

// "top-left" / "top-center" / etc. — readable by screen readers. The two
// direction words are separate keys so the composed label reads naturally in
// every locale; `alignment.*` is neutral because both designers use this grid.
function labelFor(t: TFunction, h: HAlign, v: VAlign): string {
  return t('alignment.cell', { v: t(`alignment.v.${v}`), h: t(`alignment.h.${h}`) })
}

export function AlignmentGrid({ hAlign, vAlign, onChange, disabled }: AlignmentGridProps) {
  const { t } = useTranslation()
  return (
    <div
      data-testid="alignment-grid"
      role="group"
      aria-label={t('alignment.groupLabel')}
      className="inline-grid grid-cols-3 gap-0.5 rounded border border-stone-300 p-0.5 dark:border-stone-700"
    >
      {ROWS.flatMap(v =>
        COLS.map(h => {
          const isCurrent = h === hAlign && v === vAlign
          return (
            <button
              key={`${h}-${v}`}
              type="button"
              data-testid={`alignment-cell-${h}-${v}`}
              aria-label={labelFor(t, h, v)}
              aria-pressed={isCurrent}
              disabled={disabled}
              onClick={() => onChange({ hAlign: h, vAlign: v })}
              className={[
                'flex h-6 w-6 items-center justify-center rounded-sm border text-xs leading-none',
                isCurrent
                  ? 'border-sky-500/60 bg-sky-500/30 text-sky-900 dark:text-sky-100'
                  : 'border-stone-300 bg-stone-50 text-stone-500 hover:bg-stone-200 dark:border-stone-600 dark:bg-stone-800 dark:text-stone-300 dark:hover:bg-stone-700',
                'disabled:opacity-50 disabled:hover:bg-stone-50 dark:disabled:hover:bg-stone-800',
              ].join(' ')}
            >
              {GLYPHS[`${h}-${v}`]}
            </button>
          )
        }),
      )}
    </div>
  )
}
